import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';
import GameSettingsMenu from '@baaligames/shared-ui/components/GameSettingsMenu';
import GameLoadingScreen from '@baaligames/shared-ui/components/GameLoadingScreen';
import { useAssetPreloader } from '@baaligames/shared-ui/lib/useAssetPreloader';
import { RibbonSvg, Mul2xSvg, Mul3xSvg, CoinSvg, MinusSvg, PlusSvg, CheckmarkSvg } from './Lucky7SVGs';
import { lucky7AudioEngine } from './audio';
import { api } from '@baaligames/shared-ui/lib/api';
import { startAutoSync, serverNow, isSynced } from './clockSync';
import '../ludo/ludo.css';

type BetOption = '2to6' | '7' | '8to12' | null;
type GamePhase = 'betting' | 'rolling' | 'result';

// ── Server contract (Phase 6 — Lucky 7 is now fully server-driven) ──
// The UI polls GET /api/lucky7/state ~1s and renders from it. There is NO
// client-side game logic any more (no Math.random rolls, no local pools,
// no local payouts). Bets go to POST /api/lucky7/bet; history to GET
// /api/lucky7/history. The mock simulations below are gated off.
type L7State = {
  enabled: boolean;
  maintenanceMessage?: string;
  serverNow: number;
  wallet: number;
  bettingLockMs?: number;
  roundIntervalSeconds?: number;
  round: null | {
    id: number; closesAt: number;
    pools: { '2to6': number; '7': number; '8to12': number };
    myBet: null | { card: Exclude<BetOption, null>; total: number };
  };
  lastResult: null | {
    id: number; card: Exclude<BetOption, null>; dice: [number, number];
    pools: { '2to6': number; '7': number; '8to12': number };
    settledAt: number;
    myOutcome: null | { card: Exclude<BetOption, null>; bet: number; payout: number; net: number };
  };
};
type L7HistoryRow = {
  roundId: number; settledAt: number; card: Exclude<BetOption, null>;
  dice: [number, number]; myCard: BetOption; myBet: number; myPayout: number; myNet: number;
};

interface HistoryEntry {
  id: string;
  time: string;
  date: string;
  dice: [number, number];
  bet: BetOption;
  betAmt?: number;
  winAmt: number;
}

const LUCKY7_PRELOAD_URLS = [
  '/games/lucky7/Logo.png',
  '/games/lucky7/lucky7bg.jpg',
  '/games/lucky7/button-dice-boad.png',
  '/games/lucky7/dice-boad.png',
  '/games/lucky7/Coin.png',
  '/games/ludo/Settings.png',
  '/games/ludo/Coin.png',
  '/games/ludo/Plus.png',
  '/games/lucky7/dice-roll-dice-1.png',
  '/games/lucky7/dice-roll-dice-2.png',
  '/games/lucky7/dice-roll-dice-3.png',
  '/games/lucky7/dice-roll-dice-4.png',
  '/games/lucky7/dice-roll-dice-5.png',
  '/games/lucky7/dice-roll-dice-6.png',
  '/games/lucky7/dice-roll-dice-1.gif',
  '/games/lucky7/dice-roll-dice-2.gif',
  '/games/lucky7/dice-roll-dice-3.gif',
  '/games/lucky7/dice-roll-dice-4.gif',
  '/games/lucky7/dice-roll-dice-5.gif',
  '/games/lucky7/dice-roll-dice-6.gif',
  '/games/lucky7/trophy.gif',
  '/games/lucky7/congratulation.gif',
  '/games/lucky7/gold-glitter.gif',
  '/games/lucky7/confetti.gif',
  '/games/lucky7/money-rain.gif'
];

export default function Lucky7Page() {
  return <Lucky7Game />;
}

const LUXURY_GOLD_PARTICLES = Array.from({ length: 40 }).map((_, i) => {
  const isCircle = Math.random() > 0.5;
  const colors = [
    '#ffe103', // Bright Gold
    '#ffd700', // Gold
    '#e4a333', // Amber Gold
    '#d68307', // Dark Gold
    '#ffffff', // Champagne Shine
  ];
  const color = colors[Math.floor(Math.random() * colors.length)];
  return {
    id: i,
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 4}s`,
    duration: `${2.5 + Math.random() * 2.5}s`,
    size: `${4 + Math.random() * 8}px`,
    color,
    shapeClass: isCircle ? 'rounded-full' : 'rounded-sm',
    skew: `${-15 + Math.random() * 30}deg`,
  };
});

function Lucky7Game() {
  const navigate = useNavigate();
  const dateTabsRef = useRef<HTMLDivElement>(null);
  const assetStatus = useAssetPreloader(LUCKY7_PRELOAD_URLS, 800);
  const [walletAmt, setWalletAmt] = useState(10000); 
  const [betAmount, setBetAmount] = useState<number>(250);
  // Bet bounds from /lucky7/config (admin-tunable, live). The +/- stepper clamps
  // to [minBet, min(maxBet, wallet)] so it can never climb past the house max or
  // the player's balance. Backend re-checks in model.js — this is the UX guard.
  const [minBet, setMinBet] = useState<number>(50);
  const [maxBet, setMaxBet] = useState<number>(2000);
  const [selectedOption, setSelectedOption] = useState<BetOption>(null);
  const [optionError, setOptionError] = useState(false);
  const [betError, setBetError] = useState(false);
  
  const [phase, setPhase] = useState<GamePhase>('betting');
  const [timeLeft, setTimeLeft] = useState(20);
  const [diceFaces, setDiceFaces] = useState<[number, number]>([1, 1]);
  const [winAmount, setWinAmount] = useState(0);

  // [Phase 6.1] Server-driven timing for the time displays:
  //   drawReleaseAt — epoch ms when the CURRENT round's result releases
  //                   (the round's closesAt). Shown as "DRAW TIME" + drives
  //                   the circular countdown ring.
  //   resultDrawAt  — epoch ms the SHOWN result was actually settled, so
  //                   the result modal reads "RESULT OF <that time>" (the
  //                   result a returning player sees may be 1-2 min old).
  //   roundIntervalSec — full round length, for the ring fraction + next-draw.
  const [drawReleaseAt, setDrawReleaseAt] = useState<number>(0);
  const [resultDrawAt, setResultDrawAt] = useState<number>(0);
  const [roundIntervalSec, setRoundIntervalSec] = useState<number>(180);

  // Multiplayer Bet Pools (randomly updated in effect to simulate lobby activity)
  const [pool2to6, setPool2to6] = useState(15000);
  const [pool7, setPool7] = useState(5000);
  const [pool8to12, setPool8to12] = useState(12000);
  const [hasLockedBet, setHasLockedBet] = useState(false);
  const [isFetchingResult, setIsFetchingResult] = useState(false);
  
  // Helper to generate the last 7 days
  const dateTabs = useMemo(() => {
    const days = [];
    const today = new Date();
    for (let i = 0; i < 7; i++) {
      const d = new Date();
      d.setDate(today.getDate() - i);
      days.push({
        dateStr: d.toISOString().split('T')[0],
        label: i === 0 ? 'Today' : i === 1 ? 'Yesterday' : d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })
      });
    }
    return days;
  }, []);

  const [selectedDate, setSelectedDate] = useState<string>(dateTabs[0].dateStr);

  // [Phase 6] History comes from the SERVER (GET /api/lucky7/history) —
  // every settled round in the last history_visible_days days plus the
  // player's own per-round outcome. Mapped onto the existing HistoryEntry
  // shape so the history modal + the inline strip render unchanged.
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get<{ rows: L7HistoryRow[] }>('/lucky7/history');
      setHistory((res.rows || []).map((r) => {
        const dt = new Date(r.settledAt);
        return {
          id: String(r.roundId),
          time: dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
          date: dt.toISOString().split('T')[0],
          dice: r.dice,
          bet: r.myCard,
          betAmt: r.myBet,
          winAmt: r.myPayout,
        } as HistoryEntry;
      }));
    } catch { /* keep last good history */ }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [soundOn, setSoundOn] = useState(true);

  // [Phase 6] Lobby pool simulation DISABLED — pools come from the server
  // poll (the bridge effect below). Kept as a no-op to preserve structure.

  // ─────────────────────────────────────────────────────────────────
  // SERVER BRIDGE — the single source of truth for the live round.
  // Polls /api/lucky7/state ~1s and drives every state var the JSX reads
  // (wallet, pools, phase, timeLeft, diceFaces, winAmount, hasLockedBet).
  // A short ticker derives the countdown from the server's closesAt so it
  // stays accurate regardless of poll jitter or client clock drift.
  // ─────────────────────────────────────────────────────────────────
  const roundClosesAtRef = useRef<number>(0);
  const currentRoundIdRef = useRef<number>(0);
  const shownResultIdRef = useRef<number>(0);
  const serverDriftRef = useRef<number>(0);          // serverNow - Date.now()
  const serverDriftInitRef = useRef<boolean>(false); // true once /state has set a real drift
  const roundIntervalSecRef = useRef<number>(180);   // latest round length (countdown clamp)
  const animatingRef = useRef<boolean>(false);
  const lockMsRef = useRef<number>(5000);
  const phaseRef = useRef<GamePhase>('betting');
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;

    const revealResult = (lr: NonNullable<L7State['lastResult']>) => {
      if (animatingRef.current) return;
      animatingRef.current = true;
      shownResultIdRef.current = lr.id;
      setIsFetchingResult(false);
      setDiceFaces(lr.dice);
      setResultDrawAt(lr.settledAt);   // the time THIS shown result was settled
      setPhase('rolling');
      // Roll animation → result reveal → back to betting (server opens next).
      setTimeout(() => {
        if (cancelled) return;
        setPhase('result');
        const payout = lr.myOutcome?.payout ?? 0;
        if (payout > 0) { setWinAmount(payout); lucky7AudioEngine.playWinMelody(); }
        else if (lr.myOutcome) { setWinAmount(0); lucky7AudioEngine.playLossMelody(); }
        else { setWinAmount(0); }
        lucky7AudioEngine.playDiceLand();
        loadHistory(); // refresh the inline strip + modal with the new round
        setTimeout(() => {
          if (cancelled) return;
          setWinAmount(0);
          setSelectedOption(null);
          setHasLockedBet(false);
          animatingRef.current = false;
          setPhase('betting');
        }, payout > 0 ? 6000 : 4000);
      }, 2600);
    };

    const poll = async () => {
      try {
        const st = await api.get<L7State>('/lucky7/state');
        if (cancelled) return;
        if (st.enabled === false) {
          setOptionError(false);
        } else {
          serverDriftRef.current = st.serverNow - Date.now();
          serverDriftInitRef.current = true;   // we now have a server-anchored clock fallback
          if (typeof st.bettingLockMs === 'number') lockMsRef.current = st.bettingLockMs;
          if (typeof st.roundIntervalSeconds === 'number') {
            setRoundIntervalSec(st.roundIntervalSeconds);
            roundIntervalSecRef.current = st.roundIntervalSeconds;
          }
          if (typeof st.wallet === 'number') setWalletAmt(st.wallet);
          if (st.round) {
            roundClosesAtRef.current = st.round.closesAt;
            currentRoundIdRef.current = st.round.id;
            setDrawReleaseAt(st.round.closesAt);   // when THIS round's result releases
            setPool2to6(st.round.pools['2to6']);
            setPool7(st.round.pools['7']);
            setPool8to12(st.round.pools['8to12']);
            if (st.round.myBet) { setSelectedOption(st.round.myBet.card); setHasLockedBet(true); }
          }
          // New settled result we haven't shown yet → run the reveal.
          if (st.lastResult && st.lastResult.id > shownResultIdRef.current) {
            // Show the last result's final pools while revealing.
            setPool2to6(st.lastResult.pools['2to6']);
            setPool7(st.lastResult.pools['7']);
            setPool8to12(st.lastResult.pools['8to12']);
            revealResult(st.lastResult);
          }
        }
      } catch { /* transient — keep polling */ }
      // Adaptive cadence: poll fast around the close (≤4s left, or while a
      // result is animating) so the result surfaces right as the synced
      // countdown hits zero; relaxed otherwise to spare the network.
      let nextDelay = 1200;
      const nowMs = clockNow();
      const msLeftPoll = roundClosesAtRef.current - nowMs;
      if (animatingRef.current || (msLeftPoll <= 4000 && msLeftPoll > -3000)) nextDelay = 400;
      if (!cancelled) pollTimer = setTimeout(poll, nextDelay);
    };
    poll();

    // Start continuous server-clock sync (NTP/Cristian) so the countdown is
    // anchored to the SERVER clock, not the device's. Returns a stop().
    const stopSync = startAutoSync();

    // Countdown ticker — derives timeLeft from the SYNCED server clock vs
    // the round's server-set closesAt. Immune to a wrong/jumping device clock.
    const tick = setInterval(() => {
      if (cancelled || animatingRef.current) return;
      if (phaseRef.current !== 'betting') return;
      const now = clockNow();
      const msLeft = roundClosesAtRef.current - now;
      // ── Midnight "24:00" guard ──────────────────────────────────────
      // Only derive a countdown when we have (a) a real round close time AND
      // (b) a SERVER-anchored clock (NTP sync, or at least the /state drift).
      // Without an anchor, clockNow() can fall back to the raw device clock —
      // and if the device's DATE is a day behind, closesAt(today) −
      // deviceNow(yesterday) reads ≈ 24h, which is the "24-hour countdown
      // after midnight" bug. Then HARD-CLAMP to one round length: a round
      // countdown can never legitimately exceed its interval, so no clock
      // anomaly can ever paint a multi-hour (or multi-minute) value.
      const haveAnchor = isSynced() || serverDriftInitRef.current;
      const cap = Math.max(30, roundIntervalSecRef.current || 180);
      const secLeft = (roundClosesAtRef.current > 0 && haveAnchor)
        ? Math.max(0, Math.min(cap, Math.ceil(msLeft / 1000)))
        : 0;
      setTimeLeft(secLeft);
      // When betting locks (within the lock window), show the "securing
      // result" state until the server's settled result arrives.
      if (roundClosesAtRef.current > 0 && haveAnchor && msLeft <= lockMsRef.current && hasLockedBetRef.current) {
        setIsFetchingResult(true);
      }
    }, 200);

    return () => { cancelled = true; if (pollTimer) clearTimeout(pollTimer); clearInterval(tick); stopSync(); };
  }, []);

  // Synced server clock: NTP-locked once available, else the /state drift
  // estimate, else the raw device clock (only for the first ~1s at boot).
  function clockNow(): number {
    if (isSynced()) return serverNow();
    return Date.now() + serverDriftRef.current;
  }

  // keep a ref of hasLockedBet for the ticker (avoids stale closure)
  const hasLockedBetRef = useRef(false);
  useEffect(() => { hasLockedBetRef.current = hasLockedBet; }, [hasLockedBet]);

  useEffect(() => {
    document.body.classList.add('lucky7-active');
    return () => document.body.classList.remove('lucky7-active');
  }, []);

  useEffect(() => {
    lucky7AudioEngine.toggle(soundOn);
  }, [soundOn]);

  // Scroll Nudge Effect: smooth scroll the date tabs container to suggest scrollability when history modal opens
  useEffect(() => {
    if (showHistoryModal) {
      // Reset selected date to Today (the first date tab)
      setSelectedDate(dateTabs[0].dateStr);
      // [Phase 6] Pull the freshest server history when the modal opens.
      loadHistory();

      const timer = setTimeout(() => {
        const el = dateTabsRef.current;
        if (el) {
          el.scrollTo({ left: 120, behavior: 'smooth' });
          setTimeout(() => {
            el.scrollTo({ left: 0, behavior: 'smooth' });
          }, 800);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [showHistoryModal, dateTabs]);

  useEffect(() => {
    // Sound is muted or rolling loop is stopped if fetching communal result / loading patience screen
    if (phase === 'rolling' && !isFetchingResult) {
      lucky7AudioEngine.startRollingSound();
    } else {
      lucky7AudioEngine.stopRollingSound();
    }
    return () => lucky7AudioEngine.stopRollingSound();
  }, [phase, isFetchingResult]);

  useEffect(() => {
    if (phase === 'betting' && timeLeft <= 5 && timeLeft > 0) {
      lucky7AudioEngine.playTimerTick();
    }
  }, [timeLeft, phase]);

  // One-time fetch of admin-configured bet bounds (min/max) for the stepper.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await api.get<{ bet?: { min?: number; max?: number } }>('/lucky7/config');
        if (!cancelled && cfg.bet) {
          if (cfg.bet.min) setMinBet(cfg.bet.min);
          if (cfg.bet.max) setMaxBet(cfg.bet.max);
        }
      } catch { /* keep defaults */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Keep the current stake within bounds if the admin lowers max_bet or the
  // wallet drops below it (only ever clamps DOWN; a valid stake is untouched).
  useEffect(() => {
    const ceiling = Math.max(minBet, Math.min(maxBet, walletAmt));
    setBetAmount(b => Math.min(ceiling, Math.max(minBet, b)));
  }, [minBet, maxBet, walletAmt]);

  const modifyBet = (amount: number) => {
    if (phase !== 'betting' || hasLockedBet) return;
    lucky7AudioEngine.playTap();
    // Clamp to [minBet, min(maxBet, wallet)] — never exceed the house max or balance.
    const ceiling = Math.max(minBet, Math.min(maxBet, walletAmt));
    setBetAmount(prev => Math.min(ceiling, Math.max(minBet, prev + amount)));
  };

  const currentTime = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  // [Phase 6.1] Server-driven time strings for the timing displays.
  const fmtClock = (ms: number, withSeconds = true) =>
    ms > 0
      ? new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', ...(withSeconds ? { second: '2-digit' } : {}) })
      : '--:--';
  // The time the CURRENT round's result will release (not "now").
  const drawTimeStr = fmtClock(drawReleaseAt, false);
  // The time the SHOWN result was actually settled (may be 1-2 min ago).
  // No seconds — the settle-second is noise (always ~:27) and looks odd.
  const resultDrawStr = resultDrawAt > 0 ? fmtClock(resultDrawAt, false) : currentTime;
  // Next draw = this draw + one interval.
  const nextDrawStr = drawReleaseAt > 0
    ? fmtClock(drawReleaseAt + roundIntervalSec * 1000, false)
    : '--:--';
  // Countdown as M:SS.
  const cdMin = Math.floor(Math.max(0, timeLeft) / 60);
  const cdSec = Math.max(0, timeLeft) % 60;
  const countdownStr = `${cdMin}:${String(cdSec).padStart(2, '0')}`;
  const countdownUrgent = timeLeft <= 10;
  // Ring depletion fraction (full → empty over the round).
  const ringFrac = Math.max(0, Math.min(1, timeLeft / Math.max(1, roundIntervalSec)));
  const RING_R = 20;
  const RING_C = 2 * Math.PI * RING_R;
  // The clock is the pill's right END-CAP — its diameter == the pill height,
  // so the two share the same right wall (no padding) and read as one unit.
  const PILL_H = 42;

  return (
    <div className="ludo-root lucky7-root fixed inset-0 z-[9999]">
      <GameLoadingScreen
        visible={!assetStatus.ready}
        theme="lucky7"
        progress={assetStatus.progress}
      />
      <div 
        className="app-container shadow-2xl text-white select-none font-sans"
        style={{
          background: `linear-gradient(to top, #000000f0, #260000e6 14.71%, #ff0000f0 41.6%, #1b0000fa 83.61%, #000000ff), url('/games/lucky7/lucky7bg.jpg')`,
          backgroundRepeat: 'no-repeat, repeat',
          backgroundSize: 'cover, 125px 125px',
          backgroundPosition: 'center center'
        }}
      >
        <style>{`
          /* Hide Site Chrome when game is active */
          body.lucky7-active > #root > div > nav,
          body.lucky7-active [data-site-nav],
          body.lucky7-active [data-bottom-nav] { display: none !important; }
        `}</style>

      {/* GAME'S TOP BAR (Copied verbatim from Ludo game structure and themed red) */}
      <div
        className="flex justify-between items-center gap-2 px-3 sm:px-4 pb-3 sm:pb-4 bg-gradient-to-b from-[#2a0002] to-[#1b0000] shadow-md z-[150] w-full border-b border-red-500/10 shrink-0"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
      >
        {/* Left cluster — back button (leftmost) then settings, then developer options. */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => navigate('/')}
            aria-label="Back"
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center bg-black/30 border border-white/15 hover:bg-black/50 active:scale-95 transition text-white"
          >
            <Icon name="arrow_back" size={22} filled />
          </button>
          <div className="relative">
            <button
              onClick={() => setSettingsOpen(!settingsOpen)}
              aria-label="Settings"
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shadow-[0_0_5px_rgba(0,0,0,1)] hover:scale-105 active:scale-95 transition-transform"
            >
              <img src="/games/ludo/Settings.png" className="w-full h-full object-contain" alt="Settings" />
            </button>
            <GameSettingsMenu 
              open={settingsOpen} 
              soundOn={soundOn}
              micMuted={true}
              listenMuted={true}
              voiceEnabled={false}
              onToggleSound={() => setSoundOn(!soundOn)}
              onToggleMic={() => {}}
              onToggleListen={() => {}}
              onExit={() => { setSettingsOpen(false); navigate('/'); }}
              onClose={() => setSettingsOpen(false)}
              exitLabel="Exit to Home"
              anchor="left"
            />
          </div>
        </div>

        {/* Right Wallet chip */}
        <div className="bg-gradient-to-b from-[#3a0609] to-[#260002] rounded-lg shrink-0 flex items-center justify-center pe-1 sm:pe-[0.7rem] py-[0.3rem] sm:py-[0.5rem] border border-red-500/20 shadow-[0_0_30px_rgba(239,68,68,0.05)] relative">
          <img src="/games/ludo/Coin.png" className="w-6 h-6 sm:w-7 sm:h-7 mr-2 relative scale-150 z-10" alt="Coin" />
          <span className="text-white font-bold text-base sm:text-lg mr-2 sm:mr-3 tracking-wide" id="wallet-amt">
            {Math.floor(walletAmt).toLocaleString('en-IN')}
          </span>
          <button 
            onClick={() => navigate('/wallet')}
            className="rounded-sm flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
          >
            <img src="/games/ludo/Plus.png" className="w-6 h-6 sm:w-7 sm:h-7 object-contain" alt="Add" />
          </button>
        </div>
      </div>

    <div className="w-full max-w-md px-4 flex flex-col items-center gap-2 mt-1 relative z-10 flex-1 overflow-y-auto pb-3 custom-scrollbar">
      
      {/* HEADER / RIBBON */}
      <div className="w-full flex flex-col items-center relative mt-4">
          <style>
            {`
              @import url('https://fonts.googleapis.com/css2?family=Poetsen+One&family=Carter+One&display=swap');
              .poetsen-font { font-family: "Poetsen One", sans-serif; }
              .carter-font { font-family: "Carter One", sans-serif; }
              .darktextgoldborder {
                  font-family: 'Carter One', sans-serif !important;
                  font-weight: 900 !important;
                  color: #71340f;
                  -webkit-text-stroke-width: 0.8px;
                  -webkit-text-stroke-color: #ffff00;
              }
              .goldbettingcards {
                  border: 2px solid transparent !important;
                  background: radial-gradient(circle at 50% 15%, #fff6a2 0%, #ffd034 40%, #e09b12 85%, #9d6800 100%) padding-box,
                              linear-gradient(75deg, #ffe103, #e4a333, #ffe103, #e4a333, #ffe103) border-box !important;
                  transform-style: preserve-3d !important;
              }
              .goldgradientback {
                  background: linear-gradient(135deg, #ffe103 0%, #fefaa0 25%, #e4a333 50%, #fefaa0 75%, #ffe103 100%) !important;
                  border: 1px solid #60030b !important;
                  transform-style: preserve-3d !important;
              }
              @keyframes fluctuateBoxShadow {
                  0%, 100% {
                      box-shadow: 0 0 10px 5px #00ff00;
                  }
                  50% {
                      box-shadow: 0 0 2px 2px #00ff00;
                  }
              }
              .selectedBetCard {
                  animation: fluctuateBoxShadow 1s infinite !important;
              }
              @keyframes shimmer {
                  0% {
                      transform: translateX(-100%) skewX(-12deg);
                  }
                  100% {
                      transform: translateX(200%) skewX(-12deg);
                  }
              }
              .shimmer-sweep {
                  position: relative;
                  overflow: hidden;
              }
              .shimmer-sweep::after {
                  content: '';
                  position: absolute;
                  top: 0;
                  left: 0;
                  width: 60%;
                  height: 100%;
                  background: linear-gradient(
                      to right,
                      rgba(255, 255, 255, 0) 0%,
                      rgba(255, 255, 255, 0.45) 50%,
                      rgba(255, 255, 255, 0) 100%
                  );
                  transform: translateX(-100%) skewX(-12deg);
                  animation: shimmer 2.2s infinite;
              }
              @keyframes shake {
                  0%, 100% { transform: translateX(0); }
                  20%, 60% { transform: translateX(-6px); }
                  40%, 80% { transform: translateX(6px); }
              }
              .animate-shake {
                  animation: shake 0.4s ease-in-out;
              }
              .red-error-glow {
                  box-shadow: 0 0 15px 3px rgba(239, 68, 68, 0.8) !important;
                  border-color: #ef4444 !important;
              }
              @keyframes blueGlow {
                  0%, 100% {
                      box-shadow: 0 0 14px 4px rgba(0, 102, 255, 0.95);
                  }
                  50% {
                      box-shadow: 0 0 4px 1px rgba(0, 102, 255, 0.4);
                  }
              }
              .optionErrorCard {
                  animation: blueGlow 0.6s ease-in-out infinite, shake 0.4s ease-in-out !important;
                  border: 1px solid #0066ff !important;
              }
              .gold-card-3d-parent {
                  perspective: 600px;
                  transform-style: preserve-3d;
              }
              .gold-card-3d {
                  transform-style: preserve-3d;
                  backface-visibility: hidden;
                  transition: transform 0.3s ease;
              }
              @keyframes gentle3DTilt-1 {
                  0%, 100% {
                      transform: rotateY(-14deg) rotateX(1.5deg);
                  }
                  50% {
                      transform: rotateY(14deg) rotateX(-1.5deg);
                  }
              }
              @keyframes gentle3DTilt-2 {
                  0%, 100% {
                      transform: rotateY(10deg) rotateX(1.2deg);
                  }
                  50% {
                      transform: rotateY(-10deg) rotateX(-1.2deg);
                  }
              }
              @keyframes gentle3DTilt-3 {
                  0%, 100% {
                      transform: rotateY(14deg) rotateX(-1.5deg);
                  }
                  50% {
                      transform: rotateY(-14deg) rotateX(1.5deg);
                  }
              }
              .tilt-card-1 {
                  animation: gentle3DTilt-1 6s ease-in-out infinite;
              }
              .tilt-card-2 {
                  animation: gentle3DTilt-2 5.5s ease-in-out infinite;
              }
              .tilt-card-3 {
                  animation: gentle3DTilt-3 6.5s ease-in-out infinite;
              }
              @keyframes shineTilt {
                  0%, 100% {
                      transform: translateX(-150%) skewX(-20deg);
                      opacity: 0;
                  }
                  30%, 70% {
                      opacity: 0.55;
                  }
                  50% {
                      transform: translateX(150%) skewX(-20deg);
                      opacity: 0.7;
                  }
              }
              .shine-overlay {
                  position: absolute;
                  top: 0;
                  left: 0;
                  width: 150%;
                  height: 100%;
                  background: linear-gradient(
                      to right,
                      rgba(255, 246, 186, 0) 0%,
                      rgba(255, 246, 186, 0.65) 50%,
                      rgba(255, 246, 186, 0) 100%
                  );
                  pointer-events: none;
                  z-index: 5;
              }
              .animate-shine-1 {
                  animation: shineTilt 6s ease-in-out infinite;
              }
              .animate-shine-2 {
                  animation: shineTilt 5.5s ease-in-out infinite reverse;
              }
              .animate-shine-3 {
                  animation: shineTilt 6.5s ease-in-out infinite reverse;
              }
              /* Header Ribbon Subtle 3D Tilt */
              @keyframes header3DTilt {
                  0%, 100% {
                      transform: rotateY(-3deg) rotateX(0.5deg);
                  }
                  50% {
                      transform: rotateY(3deg) rotateX(-0.5deg);
                  }
              }
              .header-3d {
                  perspective: 800px;
                  transform-style: preserve-3d;
                  animation: header3DTilt 9s ease-in-out infinite;
              }

              /* Points Container Subtle 3D Tilt */
              @keyframes points3DTilt {
                  0%, 100% {
                      transform: rotateY(4deg) rotateX(-0.5deg);
                  }
                  50% {
                      transform: rotateY(-4deg) rotateX(0.5deg);
                  }
              }
              .points-3d {
                  perspective: 600px;
                  transform-style: preserve-3d;
                  animation: points3DTilt 7.5s ease-in-out infinite;
              }

              /* Roll Dice Button Subtle 3D Tilt */
              @keyframes button3DTilt {
                  0%, 100% {
                      transform: rotateY(-5deg) rotateX(0.8deg) scale(1);
                  }
                  50% {
                      transform: rotateY(5deg) rotateX(-0.8deg) scale(1.015);
                  }
              }
              .button-3d {
                  perspective: 600px;
                  transform-style: preserve-3d;
                  animation: button3DTilt 8s ease-in-out infinite;
              }

              /* Rich Golden Gradient Borders */
              .gold-border-rich {
                  border: 1.5px solid transparent !important;
                  background: linear-gradient(to bottom, #2b0408, #110002) padding-box,
                              linear-gradient(135deg, #ffe103 0%, #e4a333 40%, #fff6a2 70%, #d68307 100%) border-box !important;
              }

              .gold-border-pill {
                  border: 1px solid transparent !important;
                  background: linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)) padding-box,
                              linear-gradient(135deg, rgba(255,225,3,0.5), rgba(228,163,51,0.2), rgba(255,246,162,0.5)) border-box !important;
              }

              .gold-border-history {
                  border: 1px solid transparent !important;
                  background: linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)) padding-box,
                              linear-gradient(135deg, #ffe103 0%, #e4a333 50%, #d68307 100%) border-box !important;
              }

              .animate-shine-points {
                  animation: shineTilt 8.5s ease-in-out infinite;
              }

              .animate-shine-button {
                  animation: shineTilt 7s ease-in-out infinite;
              }

              /* Immersive Victory Screen Animations */
              @keyframes luxuryRotate {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              @keyframes luxuryCounterRotate {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(-360deg); }
              }
              @keyframes floatingTrophy {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-10px); }
              }
              @keyframes luxuryGlitter {
                0% {
                  transform: translateY(-10px) rotate(0deg) translateX(0);
                  opacity: 0;
                }
                10% {
                  opacity: 0.95;
                }
                90% {
                  opacity: 0.95;
                }
                100% {
                  transform: translateY(105vh) rotate(360deg) translateX(25px);
                  opacity: 0;
                }
              }
              .animate-rotate-luxury {
                animation: luxuryRotate 25s linear infinite;
              }
              .animate-counter-rotate-luxury {
                animation: luxuryCounterRotate 40s linear infinite;
              }
              .animate-floating-trophy {
                animation: floatingTrophy 4s ease-in-out infinite;
              }
              .premium-victory-text {
                font-family: 'Carter One', sans-serif !important;
                background: linear-gradient(to bottom, #ffffff 0%, #fff192 30%, #ffd23f 70%, #d68307 100%);
                -webkit-background-clip: text;
                background-clip: text;
                color: transparent;
                filter: drop-shadow(0 3px 5px rgba(0, 0, 0, 0.9));
              }
              .premium-reward-text {
                font-family: 'Poetsen One', sans-serif !important;
                background: linear-gradient(to bottom, #ffffff 0%, #fff7a0 30%, #ffd034 70%, #d68307 100%);
                -webkit-background-clip: text;
                background-clip: text;
                color: transparent;
                filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.9));
              }
            `}
          </style>
          <div className="w-full max-w-[300px] flex justify-center drop-shadow-2xl px-2 header-3d" dangerouslySetInnerHTML={{ __html: RibbonSvg }} />
          
          {/* Draw timing — the RESULT-RELEASE time + a live countdown clock.
              [Phase 6.1] Server-driven: DRAW TIME is when this round's result
              is declared (not the current time), and the circular ring +
              M:SS show exactly how long is left. */}
          <div className="flex flex-col items-center mt-0.5 gap-0.5 select-none w-full">
            {/* ONE unified pill: result-release time on the left, a compact
                old-style countdown clock (two bells on top) on the right,
                split by a subtle gold divider. Trimmed in height so the
                history bar stays on-screen without scrolling. */}
            <div className="flex items-center justify-center">
              <div className="relative rounded-full gold-border-pill shadow-[inset_0_0_6px_rgba(255,225,0,0.06)] flex items-stretch overflow-hidden" style={{ height: PILL_H }}>
                {/* Result-release time — the left part, with its own rounded start. */}
                <div className="flex flex-col items-center justify-center leading-none pl-6 pr-3">
                  <span className="text-[8px] text-yellow-600/90 font-bold tracking-[0.22em] uppercase">Result at</span>
                  <span className="text-yellow-400 font-bold text-[18px] tracking-wider poetsen-font mt-0.5">
                    {drawTimeStr}
                  </span>
                </div>

                {/* subtle gold divider */}
                <div className="self-center w-px h-6 bg-gradient-to-b from-transparent via-yellow-500/35 to-transparent shrink-0" />

                {/* Countdown clock = the pill's RIGHT END-CAP. Its diameter ==
                    the pill height and it sits flush to the right edge, so the
                    pill's gold rim doubles as the clock bezel and the dark dial
                    + depleting ring + centred M:SS read as a clock built into
                    the pill. No knobs — a clean dial reads as a countdown clock. */}
                <div className="relative shrink-0" style={{ width: PILL_H, height: PILL_H }}>
                  {/* dark clock dial */}
                  <div className="absolute inset-[2px] rounded-full" style={{ background: 'radial-gradient(circle at 50% 32%, #3a1908, #110500)', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.8), inset 0 0 0 1px rgba(255,210,90,0.12)' }} />
                  {/* depleting progress ring (the countdown) */}
                  <svg className="absolute inset-0 -rotate-90" viewBox="0 0 48 48" aria-hidden="true">
                    <circle cx="24" cy="24" r={RING_R} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3" />
                    <circle
                      cx="24" cy="24" r={RING_R} fill="none"
                      stroke={countdownUrgent ? '#f87171' : '#ffe103'}
                      strokeWidth="3" strokeLinecap="round"
                      strokeDasharray={RING_C}
                      strokeDashoffset={RING_C * (1 - ringFrac)}
                      style={{
                        transition: 'stroke-dashoffset 0.5s linear, stroke 0.3s ease',
                        filter: countdownUrgent
                          ? 'drop-shadow(0 0 4px rgba(248,113,113,0.9))'
                          : 'drop-shadow(0 0 3px rgba(255,225,0,0.7))',
                      }}
                    />
                  </svg>
                  {/* M:SS centred in the dial */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`font-black text-[11px] poetsen-font tabular-nums leading-none ${countdownUrgent ? 'text-red-300 animate-pulse' : 'text-yellow-300'}`}>
                      {countdownStr}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Next Draw Pill — text vertically centred (balanced top/bottom). */}
            <div className="px-3 py-[2px] rounded-full bg-black/20 border border-white/5 opacity-50 flex items-center justify-center">
              <span className="text-red-300 font-semibold text-[9px] tracking-widest uppercase leading-none">
                NEXT DRAW: {nextDrawStr}
              </span>
            </div>
          </div>
        </div>

        <style>{`
          .custom-scrollbar::-webkit-scrollbar { display: none; }
        `}</style>

        {/* TITLE */}
        <h2 className={`
          poetsen-font text-xl mt-1 tracking-wide drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)] transition-all duration-300
          ${optionError ? 'text-red-500 animate-shake' : 'text-white'}
        `}>
          {optionError ? 'Select An Option!' : 'Select Option'}
        </h2>

        {/* BETTING CARDS */}
        <div className="flex justify-center gap-1.5 w-full mt-1 max-w-[400px]">
          <BetCard 
            id="2to6Card"
            title="2 To 6" 
            multiplier="2x"
            winPot={betAmount * 2}
            selected={selectedOption === '2to6'}
            onClick={() => { if (phase === 'betting' && !hasLockedBet) { lucky7AudioEngine.playTap(); setSelectedOption('2to6'); } }}
            disabled={phase !== 'betting' || hasLockedBet}
            hasError={optionError}
            pool={pool2to6}
          />
          <BetCard 
            id="7Card"
            title="7" 
            multiplier="3x"
            winPot={betAmount * 3}
            selected={selectedOption === '7'}
            onClick={() => { if (phase === 'betting' && !hasLockedBet) { lucky7AudioEngine.playTap(); setSelectedOption('7'); } }}
            disabled={phase !== 'betting' || hasLockedBet}
            isCenter
            hasError={optionError}
            pool={pool7}
          />
          <BetCard 
            id="8to12Card"
            title="8 To 12" 
            multiplier="2x"
            winPot={betAmount * 2}
            selected={selectedOption === '8to12'}
            onClick={() => { if (phase === 'betting' && !hasLockedBet) { lucky7AudioEngine.playTap(); setSelectedOption('8to12'); } }}
            disabled={phase !== 'betting' || hasLockedBet}
            hasError={optionError}
            pool={pool8to12}
          />
        </div>

        {/* POINTS SECTION */}
        <div className="w-full flex flex-col items-center mt-1">
          <h3 className={`
            poetsen-font text-base drop-shadow-[0_1.5px_1.5px_rgba(0,0,0,0.8)] transition-all duration-300
            ${betError ? 'text-red-500 animate-shake' : 'text-[#ffff00]'}
          `} style={{ WebkitTextStroke: '0.8px #000000' }}>
            {betError ? 'Bet Must Be > 0!' : 'COINS'}
          </h3>
          
          <div className={`
            flex items-center justify-between mt-0.5 w-[250px] h-[36px] px-1 rounded-lg transition-all duration-300
            points-3d gold-border-rich relative overflow-hidden shadow-[inset_0_2px_4px_rgba(0,0,0,0.4),0_4px_10px_rgba(0,0,0,0.5)]
            ${betError ? 'border-red-500 red-error-glow animate-shake' : ''}
          `}>
            {/* Glass Glossy Shine Overlay */}
            <div className="shine-overlay animate-shine-points" />
            
            <button 
              onClick={() => modifyBet(-50)}
              disabled={phase !== 'betting' || betAmount <= minBet}
              className="w-[28px] h-[28px] bg-gradient-to-b from-[#fff6a2] via-[#ffd034] to-[#d68307] border border-[#804c00] rounded-md flex items-center justify-center text-[#4a1d00] active:scale-95 transition-transform disabled:opacity-50 shadow-[0_2px_4px_rgba(0,0,0,0.4),inset_0_1px_1.5px_rgba(255,255,255,0.45)] hover:brightness-105 relative z-10"
              style={{ padding: '0px' }}
            >
              <div className="w-4 h-4 flex items-center justify-center" dangerouslySetInnerHTML={{ __html: MinusSvg }} />
            </button>
            
            <div className="flex-1 h-[28px] mx-2 px-3 bg-black/50 border border-yellow-600/10 shadow-[inset_0_2px_4px_rgba(0,0,0,0.8)] rounded-full flex items-center justify-center gap-1 relative z-10">
              <img src="/games/lucky7/Coin.png" alt="Coin" className="w-[16px] h-[16px] object-contain drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]" />
              <span className="text-[#ffde03] font-black text-base poetsen-font tracking-wider drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">{betAmount}</span>
            </div>
            
            <button 
              onClick={() => modifyBet(50)}
              disabled={phase !== 'betting' || betAmount >= Math.min(maxBet, walletAmt)}
              className="w-[28px] h-[28px] bg-gradient-to-b from-[#fff6a2] via-[#ffd034] to-[#d68307] border border-[#804c00] rounded-md flex items-center justify-center text-[#4a1d00] active:scale-95 transition-transform disabled:opacity-50 shadow-[0_2px_4px_rgba(0,0,0,0.4),inset_0_1px_1.5px_rgba(255,255,255,0.45)] hover:brightness-105 relative z-10"
              style={{ padding: '0px' }}
            >
              <div className="w-4 h-4 flex items-center justify-center" dangerouslySetInnerHTML={{ __html: PlusSvg }} />
            </button>
          </div>
        </div>

        {/* ROLL DICE BUTTON AREA */}
        <div className="w-full flex flex-col items-center mt-1">
          <button 
            onClick={() => {
              if (phase !== 'betting' || hasLockedBet) return;
              lucky7AudioEngine.playTap();
              let hasError = false;
              if (!selectedOption) {
                setOptionError(true);
                hasError = true;
                setTimeout(() => setOptionError(false), 1200);
              }
              if (betAmount <= 0) {
                setBetError(true);
                hasError = true;
                setTimeout(() => setBetError(false), 1200);
              }
              if (hasError) return;

              // [Phase 6] Place the bet on the SERVER. The atomic wallet
              // debit + one-card-per-round + caps are all enforced there;
              // on success we lock the UI and reflect the new balance. The
              // next /state poll keeps pools + myBet in sync.
              const card = selectedOption;
              const amount = betAmount;
              api.post<{ newBalance?: number }>('/lucky7/bet', { card, amount })
                .then((r) => {
                  setHasLockedBet(true);
                  if (typeof r?.newBalance === 'number') setWalletAmt(r.newBalance);
                })
                .catch(() => {
                  setBetError(true);
                  setTimeout(() => setBetError(false), 1800);
                });
            }}
            disabled={phase !== 'betting' || hasLockedBet}
            className={`border-0 bg-transparent active:scale-95 transition-all cursor-pointer drop-shadow-lg ${
              (hasLockedBet) ? 'opacity-50 cursor-not-allowed filter grayscale' : ''
            }`}
          >
            <div className="button-3d relative p-[1px]">
              <img 
                src="/games/lucky7/button-dice-boad.png" 
                alt="Place Bet" 
                className="w-[145px] h-auto object-contain" 
              />
            </div>
          </button>

          <div className="w-full flex flex-col items-center mt-2">
            <div className="flex justify-center flex-nowrap gap-1.5 mb-0 select-none">
              {history.slice(0, 8).reverse().map((val, idx) => {
                const total = val.dice[0] + val.dice[1];
                const isSeven = total === 7;
                return (
                  <div 
                    key={idx} 
                    className={`w-[32px] h-[32px] flex items-center justify-center font-black text-base rounded-md shadow-md poetsen-font border transition-all duration-300
                      ${isSeven 
                        ? 'bg-gradient-to-b from-[#801000] to-[#4d0800] border-[#ffe103] text-yellow-300 shadow-[0_0_8px_rgba(250,204,21,0.5),inset_0_1px_2px_rgba(255,255,255,0.2)]' 
                        : 'bg-gradient-to-b from-[#3a060a] to-[#150002] border-[#ffe103]/65 text-[#ffde03] shadow-[0_2px_5px_rgba(0,0,0,0.75),inset_0_1px_2px_rgba(255,255,255,0.15)]'
                      }`}
                  >
                    {total}
                  </div>
                );
              })}
              {/* Pending round outcome placeholder */}
              <div className="w-[32px] h-[32px] flex items-center justify-center font-black text-base rounded-md border border-yellow-400 bg-gradient-to-b from-[#ffd200] to-[#d68307] text-[#4d0800] shadow-[0_0_8px_rgba(250,204,21,0.3)] animate-pulse">
                ?
              </div>
            </div>
            <button 
              onClick={() => { lucky7AudioEngine.playTap(); setShowHistoryModal(true); }}
              className="mt-1.5 px-5 py-1 bg-gradient-to-b from-[#ffd200] via-[#ffde03] to-[#d68307] text-[#3d1200] text-[11px] font-extrabold tracking-wider uppercase rounded-full shadow-[0_4px_10px_rgba(0,0,0,0.5),0_0_6px_rgba(255,225,3,0.2)] border border-[#ffe103] hover:brightness-110 active:scale-95 transition-all"
            >
              → See All History ←
            </button>
          </div>
          </div>
        </div>

      {/* RESULT MODAL OVERLAY */}
      {(phase === 'rolling' || phase === 'result') && (
        <div className="absolute inset-0 z-50 flex items-center justify-center animate-in fade-in duration-300 pointer-events-none">
          {/* Main repeating damask background pattern with dark red overlay (exact match to main screen) */}
          <div className="absolute inset-0 z-0" 
               style={{
                 background: `linear-gradient(to top, #000000f0, #260000e6 14.71%, #ff0000f0 41.6%, #1b0000fa 83.61%, #000000ff), url('/games/lucky7/lucky7bg.jpg')`,
                 backgroundRepeat: 'no-repeat, repeat',
                 backgroundSize: 'cover, 125px 125px',
                 backgroundPosition: 'center center'
               }} 
          />
               {phase === 'result' && winAmount > 0 ? (
            <>
              {/* Fullscreen falling gold particles */}
              <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none z-40">
                {LUXURY_GOLD_PARTICLES.map((p) => (
                  <div
                    key={p.id}
                    className={`absolute ${p.shapeClass} opacity-0`}
                    style={{
                      left: p.left,
                      top: '-20px',
                      width: p.size,
                      height: p.size,
                      backgroundColor: p.color,
                      boxShadow: `0 0 10px ${p.color}`,
                      transform: `skewX(${p.skew})`,
                      animation: `luxuryGlitter ${p.duration} linear infinite`,
                      animationDelay: p.delay,
                    }}
                  />
                ))}
              </div>

              {/* Celebration Congratulations Cardless Stage */}
              <div 
                className="absolute inset-0 z-40 select-none animate-in fade-in zoom-in-95 duration-500"
                style={{
                  transformStyle: 'preserve-3d',
                  perspective: '1000px'
                }}
              >
                {/* Elegant Header Title */}
                <div className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 z-10 animate-in slide-in-from-top-12 duration-700" style={{ top: 'calc(env(safe-area-inset-top) + 4.5rem)' }}>
                  <span 
                    className="text-[11px] uppercase tracking-[0.4em] font-black text-yellow-400/90 text-center"
                    style={{ textShadow: '0 0 10px rgba(255,225,0,0.5)' }}
                  >
                    ✦ Y O U   W O N ✦
                  </span>
                  <h1 className="text-4xl sm:text-5xl uppercase leading-none tracking-widest premium-victory-text">
                    VICTORY!
                  </h1>
                </div>

                {/* Nested Concentric Rotating Gold Rings Pedestal */}
                <div className="absolute top-[32%] left-1/2 -translate-x-1/2 w-64 h-64 flex items-center justify-center z-10">
                  {/* Glowing Radial Light Behind Rings */}
                  <div className="absolute w-[320px] h-[320px] rounded-full bg-yellow-500/10 blur-[75px] animate-pulse z-0 pointer-events-none" />

                  {/* Outer Wreath Ring (Rotates Counter-Clockwise) */}
                  <svg className="absolute w-full h-full animate-counter-rotate-luxury opacity-40 pointer-events-none" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="48" fill="none" stroke="url(#goldGrad)" strokeWidth="0.5" strokeDasharray="1 8" />
                    <circle cx="50" cy="50" r="45" fill="none" stroke="url(#goldGrad)" strokeWidth="0.75" strokeDasharray="6 3 1 3" />
                    <defs>
                      <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#fff6a2" />
                        <stop offset="50%" stopColor="#ffd034" />
                        <stop offset="100%" stopColor="#d68307" />
                      </linearGradient>
                    </defs>
                  </svg>

                  {/* Inner Wreath Ring (Rotates Clockwise) */}
                  <svg className="absolute w-[86%] h-[86%] animate-rotate-luxury opacity-70 pointer-events-none" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="url(#goldGrad)" strokeWidth="1.25" strokeDasharray="12 4 2 4" />
                    <circle cx="50" cy="50" r="38" fill="none" stroke="url(#goldGrad)" strokeWidth="0.5" strokeDasharray="3 3" />
                    <g fill="#ffd034" filter="drop-shadow(0 0 3px rgba(255,208,52,0.6))">
                      {/* Accent Stars on the ring */}
                      <path d="M50,3 L51.2,5.2 L53.5,5.5 L51.8,7.2 L52.2,9.5 L50,8.3 L47.8,9.5 L48.2,7.2 L46.5,5.5 L48.8,5.2 Z" />
                      <path d="M50,97 L51.2,94.8 L53.5,94.5 L51.8,92.8 L52.2,90.5 L50,91.7 L47.8,90.5 L48.2,92.8 L46.5,94.5 L48.8,94.8 Z" />
                      <path d="M3,50 L5.2,51.2 L5.5,53.5 L7.2,51.8 L9.5,52.2 L8.3,50 L9.5,47.8 L7.2,48.2 L5.5,46.5 L5.2,48.8 Z" />
                      <path d="M97,50 L94.8,51.2 L94.5,53.5 L92.8,51.8 L90.5,52.2 L91.7,50 L90.5,47.8 L92.8,48.2 L94.5,46.5 L94.8,48.8 Z" />
                    </g>
                  </svg>

                  {/* Floating Trophy GIF inside rings */}
                  <div className="absolute w-[72%] h-[72%] flex items-center justify-center animate-floating-trophy z-10">
                    <img 
                      src="/games/lucky7/trophy.gif" 
                      className="w-[120px] h-[120px] object-contain select-none filter drop-shadow-[0_8px_20px_rgba(255,215,0,0.6)]" 
                      alt="Trophy"
                    />
                  </div>
                </div>

                {/* Massive Freestanding Glow Amount */}
                <div className="absolute bottom-[22%] left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 z-10 animate-in slide-in-from-bottom-12 duration-700 delay-100">
                  <span className="text-[11px] uppercase tracking-[0.3em] text-[#ffde03]/75 font-black text-center">
                    REWARD RECEIVED
                  </span>
                  <div className="flex items-center justify-center gap-2 drop-shadow-[0_8px_20px_rgba(0,0,0,0.95)]">
                    <span className="text-6xl sm:text-7xl font-black tracking-tighter premium-reward-text">
                      <span className="text-4xl sm:text-5xl font-bold align-middle mr-1">₹</span>
                      {winAmount.toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>

                {/* Minimalist Status Badge Pill */}
                <div className="absolute bottom-[8%] left-1/2 -translate-x-1/2 flex items-center gap-2 py-1.5 px-5 rounded-full border border-green-500/30 bg-green-500/10 shadow-[inset_0_1px_3px_rgba(16,185,129,0.1),0_4px_12px_rgba(0,0,0,0.5)] z-10 animate-in fade-in duration-1000 delay-300">
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse shadow-[0_0_8px_#10b981]" />
                  <span className="text-green-400 font-black text-[9px] tracking-widest uppercase">
                    Balance updated successfully
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="relative z-10 flex flex-col items-center w-full max-w-md">
              {/* Header section with glassmorphism and gold accents */}
              <div className="flex flex-col items-center mb-4 select-none scale-100">
                <span 
                  className="text-xs uppercase tracking-[0.25em] mb-1 font-bold"
                  style={{
                    color: '#ffe103',
                    textShadow: '0 0 10px rgba(255, 225, 0, 0.4)'
                  }}
                >
                  ✦ Result Announcement ✦
                </span>
                
                <div 
                  className="px-5 py-1.5 rounded-xl bg-black/60 backdrop-blur-md border border-yellow-500/40 shadow-[0_0_20px_rgba(0,0,0,0.8),inset_0_0_12px_rgba(255,225,0,0.15)] flex flex-col items-center gap-0.5"
                >
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                    Result Of
                  </span>
                  <span
                    className="text-xl font-black poetsen-font tracking-wide"
                    style={{
                      color: '#ffe103',
                      textShadow: '0 2px 4px rgba(0,0,0,0.8)'
                    }}
                  >
                    {resultDrawStr}
                  </span>
                </div>
              </div>
              
              {/* The Wooden Board - Dice Centered Perfectly inside the red felt */}
              <div className="relative w-[380px] h-[190px] flex items-center justify-center"
                   style={{
                     background: "url('/games/lucky7/dice-boad.png') no-repeat center center",
                     backgroundSize: '100% 100%'
                   }}>
                
                <div className="flex gap-2 items-center" style={{ transform: 'translate(-14px, -38px)' }}>
                  {/* DICE 1 */}
                  <div className="w-[120px] h-[120px] flex items-center justify-center drop-shadow-2xl">
                    {phase === 'rolling' ? (
                      <img src={`/games/lucky7/dice-roll-dice-${diceFaces[0]}.gif`} alt="Rolling" className="w-full h-full object-contain" />
                    ) : (
                      <img src={`/games/lucky7/dice-roll-dice-${diceFaces[0]}.png`} alt={`Dice ${diceFaces[0]}`} className="w-full h-full object-contain animate-in zoom-in spin-in-12 duration-500" />
                    )}
                  </div>
                  
                  {/* DICE 2 */}
                  <div className="w-[120px] h-[120px] flex items-center justify-center drop-shadow-2xl">
                    {phase === 'rolling' ? (
                      <img src={`/games/lucky7/dice-roll-dice-${diceFaces[1]}.gif`} alt="Rolling" className="w-full h-full object-contain" />
                    ) : (
                      <img src={`/games/lucky7/dice-roll-dice-${diceFaces[1]}.png`} alt={`Dice ${diceFaces[1]}`} className="w-full h-full object-contain animate-in zoom-in spin-in-12 duration-500 delay-100" />
                    )}
                  </div>
                </div>
              </div>

              {/* Result Info Area - Fixed Height to Prevent Layout Shift */}
              <div className="w-full h-[140px] flex flex-col items-center justify-center mt-3">
                {phase === 'result' && (
                  <div className="flex flex-col items-center justify-center animate-in fade-in duration-500 select-none">
                    {/* Equation */}
                    <div className="flex items-center justify-center gap-4 poetsen-font">
                      <span 
                        className="text-5xl font-black drop-shadow-[0_4px_4px_rgba(0,0,0,0.9)]" 
                        style={{ color: '#ffe103', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
                      >
                        {diceFaces[0]}
                      </span>
                      <span className="text-2xl font-bold text-gray-300 drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]">+</span>
                      <span 
                        className="text-5xl font-black drop-shadow-[0_4px_4px_rgba(0,0,0,0.9)]" 
                        style={{ color: '#ffe103', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
                      >
                        {diceFaces[1]}
                      </span>
                      <span className="text-2xl font-bold text-gray-300 drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]">=</span>
                      <div 
                        className="px-4 py-1.5 rounded-2xl bg-black border-2 border-[#ffe103] shadow-[0_0_15px_rgba(255,225,0,0.4)] flex items-center justify-center"
                      >
                        <span 
                          className="text-5xl font-black" 
                          style={{ color: '#ffe103', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
                        >
                          {diceFaces[0] + diceFaces[1]}
                        </span>
                      </div>
                    </div>

                    {/* This Time Result Styled Banner */}
                    <div className="mt-2 flex flex-col items-center animate-in zoom-in duration-500 delay-150">
                      <span className="text-[9px] font-bold text-gray-400 tracking-[0.25em] uppercase mb-0.5 drop-shadow-md">
                        This Time Result
                      </span>
                      <span 
                        className="text-3xl font-extrabold carter-font tracking-wide uppercase drop-shadow-[0_4px_6px_rgba(0,0,0,0.9)] px-5 py-1 rounded-xl border border-yellow-500/20 bg-black/40 shadow-[inset_0_0_10px_rgba(255,225,0,0.05)]"
                        style={{
                          color: '#ffe103',
                          WebkitTextStroke: '1.5px #60030b',
                          textShadow: '0 2px 4px rgba(0, 0, 0, 0.9)'
                        }}
                      >
                        {(() => {
                          const total = diceFaces[0] + diceFaces[1];
                          if (total === 7) return '7';
                          if (total >= 2 && total <= 6) return '2 To 6';
                          return '8 To 12';
                        })()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* HISTORY MODAL (Visual & Detailed Layout) */}
      {showHistoryModal && (() => {
        const selectedDayEntries = history.filter(h => h.date === selectedDate);
        const totalBet = selectedDayEntries.reduce((sum, h) => sum + (h.betAmt || 0), 0);
        const totalWin = selectedDayEntries.reduce((sum, h) => sum + (h.winAmt || 0), 0);
        const netPnL = totalWin - totalBet;
        
        return (
          <div className="absolute inset-0 z-[250] flex items-center justify-center bg-black/85 backdrop-blur-sm p-3 sm:p-4 animate-in fade-in select-none">
            <div className="bg-[#4d0c00] border-2 border-[#ffd034] rounded-2xl w-full max-w-md overflow-hidden shadow-[0_0_30px_rgba(255,208,52,0.25)] flex flex-col max-h-[82vh]">
              <div className="p-4 border-b border-[#ffd034]/30 flex-shrink-0 flex justify-between items-center bg-[#290001]/90 relative">
                {/* Royal Crown Icon next to Title */}
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-yellow-400 drop-shadow-[0_0_6px_rgba(255,225,3,0.6)]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2 19h20v2H2v-2zm1-2h18L18 7l-4 4-2-6-2 6-4-4L3 17z" />
                  </svg>
                  <h3 className="text-lg sm:text-xl font-black text-yellow-300 poetsen-font tracking-wide" style={{ textShadow: '0 0 12px rgba(255,208,52,0.5)' }}>Game History</h3>
                </div>
                <button 
                  onClick={() => { lucky7AudioEngine.playTap(); setShowHistoryModal(false); }} 
                  className="text-yellow-400 hover:text-yellow-200 text-3xl leading-none transition active:scale-95"
                >
                  &times;
                </button>
              </div>

              {/* Last 7 Days Filter Tabs */}
              <div ref={dateTabsRef} className="flex-shrink-0 flex gap-1.5 overflow-x-auto px-4 py-2 bg-[#2d0001]/95 border-b border-[#ffd034]/20 custom-scrollbar-thin">
                {dateTabs.map((tab) => {
                  const isActive = tab.dateStr === selectedDate;
                  return (
                    <button
                      key={tab.dateStr}
                      onClick={() => { lucky7AudioEngine.playTap(); setSelectedDate(tab.dateStr); }}
                      className={`flex-shrink-0 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider poetsen-font border transition-all active:scale-95 ${
                        isActive
                          ? 'bg-gradient-to-b from-[#ffe103] via-[#ffd700] to-[#d68307] text-[#3d1200] border-[#ffe103] shadow-[0_0_10px_rgba(255,225,3,0.5)]'
                          : 'bg-black/45 text-yellow-500/80 border-[#ffd034]/20 hover:border-[#ffd034]/40 hover:text-yellow-400'
                      }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* Summary Bar */}
              <div className="px-4 py-2 bg-[#1f0001] border-b border-[#ffd034]/15 flex-shrink-0 flex items-center justify-between text-[11px] font-bold text-gray-400">
                <div className="flex gap-2.5">
                  <span>Bet: <strong className="text-white">₹{totalBet}</strong></span>
                  <span>Win: <strong className="text-white">₹{totalWin}</strong></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span>P&L:</span>
                  <span className={`poetsen-font font-black text-[11px] px-2 py-0.5 rounded border leading-none ${
                    netPnL > 0 ? 'bg-green-500/10 text-green-400 border-green-500/20' :
                    netPnL < 0 ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                    'bg-gray-500/10 text-gray-400 border-gray-500/20'
                  }`}>
                    {netPnL > 0 ? `+₹${netPnL}` : netPnL < 0 ? `-₹${Math.abs(netPnL)}` : '₹0'}
                  </span>
                </div>
              </div>

              <div className="overflow-y-auto p-3 flex flex-col gap-2 bg-[#2d0001]/40 custom-scrollbar flex-1">
                {selectedDayEntries.length === 0 ? (
                  <div className="text-center py-10 text-gray-400 font-bold">No history available for this day</div>
                ) : (
                  selectedDayEntries.map((h, i) => (
                    <div 
                      key={h.id} 
                      className="flex items-center justify-between bg-black/45 border border-[#ffd034]/15 p-3 rounded-xl gap-2 hover:border-[#ffd034]/30 transition"
                    >
                      {/* Left: Time and Bet Option */}
                      <div className="flex flex-col gap-1 w-[28%] min-w-[70px]">
                        <span className="text-[10px] text-yellow-400/60 font-semibold">{h.time}</span>
                        <span className={`text-[10px] font-black uppercase tracking-wider poetsen-font px-1.5 py-0.5 rounded text-center border leading-none ${
                          h.bet === '2to6' ? 'bg-yellow-500/10 text-yellow-300 border-yellow-500/20' :
                          h.bet === '7' ? 'bg-amber-500/10 text-amber-300 border-amber-500/20' :
                          h.bet === '8to12' ? 'bg-yellow-500/10 text-yellow-300 border-yellow-500/20' :
                          'bg-gray-500/10 text-gray-400 border-gray-500/20'
                        }`}>
                          {h.bet === '2to6' ? '2 To 6' :
                           h.bet === '7' ? '7' :
                           h.bet === '8to12' ? '8 To 12' :
                           'No Bet'}
                        </span>
                      </div>

                      {/* Center: Dice Visual formula */}
                      <div className="flex items-center gap-1.5 bg-[#1a1c1e] px-2.5 py-1.5 rounded-xl border border-white/5 shadow-[inset_0_2px_4px_rgba(0,0,0,0.8)]">
                        <div className="flex gap-1">
                          <RenderDie value={h.dice[0]} />
                          <RenderDie value={h.dice[1]} />
                        </div>
                        <span className="text-white/80 text-sm font-black font-sans mx-0.5">=</span>
                        <span className="text-[#ffd034] font-black text-base sm:text-lg poetsen-font px-0.5 drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]">
                          {h.dice[0] + h.dice[1]}
                        </span>
                      </div>

                      {/* Right: Profit/Loss detail */}
                      <div className="flex flex-col items-end justify-center w-[34%] min-w-[85px] gap-1">
                        {(() => {
                          const betAmt = h.betAmt ?? 0;
                          const winAmt = h.winAmt ?? 0;
                          if (betAmt > 0) {
                            if (winAmt > 0) {
                              const profit = winAmt - betAmt;
                              return (
                                <>
                                  <div className="flex items-center gap-1 bg-green-500/10 border border-green-500/35 px-2 py-0.5 rounded-full shadow-sm">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
                                    <span className="font-extrabold text-[11px] text-green-400 poetsen-font tracking-wide">
                                      +₹{profit}
                                    </span>
                                  </div>
                                  <div className="text-[8px] text-gray-400 font-medium text-right leading-none flex flex-col gap-0.5">
                                    <span>Bet: ₹{betAmt}</span>
                                    <span>Win: ₹{winAmt}</span>
                                  </div>
                                </>
                              );
                            } else {
                              return (
                                <>
                                  <div className="flex items-center gap-1 bg-red-500/10 border border-red-500/35 px-2 py-0.5 rounded-full shadow-sm">
                                    <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                                    <span className="font-extrabold text-[11px] text-red-400 poetsen-font tracking-wide">
                                      -₹{betAmt}
                                    </span>
                                  </div>
                                  <div className="text-[8px] text-gray-400 font-medium text-right leading-none flex flex-col gap-0.5">
                                    <span>Bet: ₹{betAmt}</span>
                                    <span>Win: ₹0</span>
                                  </div>
                                </>
                              );
                            }
                          } else {
                            return (
                              <>
                                <div className="flex items-center gap-1 bg-gray-500/10 border border-gray-500/20 px-2 py-0.5 rounded-full">
                                  <span className="w-1.5 h-1.5 rounded-full bg-gray-500"></span>
                                  <span className="font-bold text-[10px] text-gray-400 poetsen-font tracking-wide">
                                    Spectator
                                  </span>
                                </div>
                                <span className="text-[8px] text-gray-500 text-right font-medium">
                                  No Bet Placed
                                </span>
                              </>
                            );
                          }
                        })()}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* GLASSMORPHIC BOTTOM PANEL OVERLAY (LOCKED BETS & SERVER DELAY STATES) */}
      {(((hasLockedBet) && phase === 'betting') || isFetchingResult) && (
        <div className="absolute bottom-0 left-0 right-0 h-[48%] bg-black/60 backdrop-blur-md border-t border-yellow-500/30 rounded-t-3xl z-40 flex flex-col items-center justify-center p-4 text-center animate-in slide-in-from-bottom duration-500 select-none shadow-[0_-10px_30px_rgba(0,0,0,0.8)]">
          {isFetchingResult ? (
            /* Securing Result / Patience Content (Hand Icon) */
            <div className="flex flex-col items-center animate-in fade-in duration-300">
              {/* Glowing Bouncing Hand Badge */}
              <div className="w-16 h-16 rounded-full bg-gradient-to-b from-[#ffd200] to-[#d68307] border-2 border-[#ffe103] flex items-center justify-center shadow-[0_0_25px_rgba(251,191,36,0.5)] animate-bounce mb-3">
                <span className="text-3xl">✋</span>
              </div>
              
              <h3 className="poetsen-font text-xl text-yellow-400 tracking-wide uppercase" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                Securing Result
              </h3>
              
              <p className="text-xs text-gray-300 max-w-[260px] mt-1.5 leading-relaxed">
                Please have patience. Fetching the official multiplayer draw result from the server...
              </p>
              
              {/* Connecting Spinner Pill */}
              <div className="mt-4 px-4 py-1.5 rounded-full bg-black/40 border border-yellow-500/30 flex items-center gap-2 shadow-[inset_0_2px_4px_rgba(0,0,0,0.6)]">
                <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                <span className="text-[10px] text-yellow-300 font-bold uppercase tracking-wider poetsen-font">
                  Connecting to server...
                </span>
              </div>
            </div>
          ) : (
            /* Bet Registered Content (Lock Icon) */
            <div className="flex flex-col items-center animate-in fade-in duration-300">
              {/* Lock Icon */}
              <div className="w-12 h-12 rounded-full bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center mb-3 shadow-[0_0_15px_rgba(234,179,8,0.2)] animate-pulse">
                <Icon name="lock" size={24} className="text-yellow-400" filled />
              </div>
              
              <h3 className="poetsen-font text-xl text-yellow-400 tracking-wide uppercase" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>
                Bet Locked
              </h3>
              
              <p className="text-xs text-gray-300 max-w-[260px] mt-1.5 leading-relaxed">
                Your bet of <span className="text-yellow-300 font-bold">₹{betAmount}</span> on <span className="text-yellow-300 font-bold uppercase">{selectedOption === '2to6' ? '2 To 6' : selectedOption === '7' ? '7' : '8 To 12'}</span> is registered.
              </p>
              
              <div className="mt-4 px-4 py-1.5 rounded-full bg-black/40 border border-white/5 flex items-center gap-2">
                <span className="text-[10px] text-gray-400 uppercase tracking-widest">Waiting for draw in</span>
                <span className="text-sm font-black text-yellow-400 poetsen-font">{timeLeft}s</span>
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}

function BetCard({ id, title, multiplier, winPot, selected, onClick, disabled, isCenter, hasError, pool }: { 
  id: string;
  title: string; 
  multiplier: string; 
  winPot: number;
  selected: boolean; 
  onClick: () => void; 
  disabled: boolean;
  isCenter?: boolean;
  hasError?: boolean;
  pool: number;
}) {
  let tiltClass = 'tilt-card-1';
  let shineClass = 'animate-shine-1';
  if (isCenter) {
    tiltClass = 'tilt-card-2';
    shineClass = 'animate-shine-2';
  } else if (title.includes('8')) {
    tiltClass = 'tilt-card-3';
    shineClass = 'animate-shine-3';
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        relative flex flex-col items-center transition-all duration-200
        w-1/3 max-w-[130px] gold-card-3d-parent
        ${disabled && !selected ? 'opacity-80 cursor-not-allowed' : 'active:scale-95 hover:brightness-110'}
      `}
    >
      <div 
        id={id}
        className={`
          w-full flex flex-col items-center transition-all duration-300
          gold-card-3d ${tiltClass}
        `}
      >
        {/* Decorative Top Tab (SVG) - Styled behind the card (z-0 relative) so only the top curves and text are visible */}
        <div className="w-[60px] h-[36px] flex items-center justify-center z-0 relative" dangerouslySetInnerHTML={{ __html: multiplier === '3x' ? Mul3xSvg : Mul2xSvg }} />

        {/* Outer Card Wrapper (goldgradientback) - z-10 relative overlays the bottom portion of the badge */}
        <div 
          className={`
            w-full rounded-2xl goldgradientback p-[2px] z-10 relative -mt-[1px] transition-all
            ${selected ? 'selectedBetCard' : ''}
            ${hasError ? 'optionErrorCard' : ''}
          `}
          style={{ transformStyle: 'preserve-3d' }}
        >
          {/* Inner Card (goldbettingcards) */}
          <div 
            className="w-full rounded-2xl goldbettingcards px-0 py-1 flex flex-col items-center relative"
            style={{ transformStyle: 'preserve-3d' }}
          >
            {/* Clipped container for shine overlay to prevent border leaks */}
            <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-0">
              <div className={`shine-overlay ${shineClass}`} />
            </div>

            <div className="h-10 flex flex-col justify-center mb-1">
              <b className="my-0 leading-none whitespace-nowrap darktextgoldborder"
                 style={{
                   fontSize: '1.75rem',
                   letterSpacing: '-2px',
                 }}>
                {title}
              </b>
            </div>

            {/* Center Stripe & Selection Marker Wrapper */}
            <div className="w-full flex items-center justify-center relative my-1 h-[53px]">
              {/* Subtle Dark Gold Center Stripe */}
              <div 
                className="absolute left-0 right-0 h-[52px] z-0 pointer-events-none" 
                style={{
                  background: 'linear-gradient(to right, rgba(135, 80, 10, 0.15) 0%, rgba(135, 80, 10, 0.22) 50%, rgba(135, 80, 10, 0.15) 100%)',
                  borderTop: '1.5px solid rgba(113, 52, 15, 0.16)',
                  borderBottom: '1.5px solid rgba(113, 52, 15, 0.16)'
                }}
              />

              {/* Circle / Selection marker - 3D Recessed Hole with Beveled Gold Rim */}
              <div 
                className="w-[45px] h-[45px] rounded-full border-[3px] border-t-[#fff6a2] border-r-[#ffd034] border-b-[#d68307] border-l-[#ffd034] bg-gradient-to-b from-[#4a0a0f] to-[#250305] flex items-center justify-center relative z-10 transition-all"
                style={{
                  boxShadow: selected 
                    ? 'inset 0 3px 6px rgba(0,0,0,0.85), 0 1.5px 2px rgba(0,0,0,0.3), 0 0 15px rgba(0,255,0,0.6)' 
                    : 'inset 0 3px 6px rgba(0,0,0,0.85), 0 1.5px 2px rgba(0,0,0,0.3)',
                  transformStyle: 'preserve-3d'
                }}
              >
                {/* Checkmark (floats at Z = 10px in front of the recessed hole) */}
                {selected && (
                  <div 
                    className="w-[28px] h-[28px] flex items-center justify-center z-10 drop-shadow-[0_2px_3px_rgba(0,0,0,0.85)] [&>svg]:w-full [&>svg]:h-full"
                    style={{
                      transform: 'translateZ(10px) scale(0.95)',
                      transformStyle: 'preserve-3d',
                      position: 'absolute'
                    }}
                    dangerouslySetInnerHTML={{ __html: CheckmarkSvg }} 
                  />
                )}
              </div>
            </div>

            {/* Simulated Lobby Pool Total Badge */}
            <div className="text-lg leading-none mb-1 mt-1 darktextgoldborder">
              WIN
            </div>

            {/* Amount Box */}
            <div className="flex w-[90%] items-center justify-between mt-auto mb-1 px-1 py-0.5 rounded-md border border-[#ffff00] bg-[#552800]">
              <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center" dangerouslySetInnerHTML={{ __html: CoinSvg }} />
              <span className="text-white font-bold text-[14px] leading-none text-right w-full">{winPot}</span>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

function RenderDie({ value }: { value: number }) {
  const dots: Record<number, [number, number][]> = {
    1: [[18, 18]],
    2: [[10, 10], [26, 26]],
    3: [[10, 10], [18, 18], [26, 26]],
    4: [[10, 10], [26, 10], [10, 26], [26, 26]],
    5: [[10, 10], [26, 10], [18, 18], [10, 26], [26, 26]],
    6: [[10, 10], [26, 10], [10, 18], [26, 18], [10, 26], [26, 26]]
  };
  const currentDots = dots[value] || [];
  return (
    <svg className="w-[26px] h-[26px] sm:w-7 sm:h-7 rounded-[4px] shadow-[0_2px_4px_rgba(0,0,0,0.3)] border border-gray-300 flex-shrink-0" viewBox="0 0 36 36">
      <rect width="36" height="36" rx="5" fill="white" />
      {currentDots.map(([cx, cy], idx) => (
        <circle key={idx} cx={cx} cy={cy} r="3" fill="#1f0003" />
      ))}
    </svg>
  );
}

